import React, { createContext, useState, useEffect, useContext } from 'react';
import { AuthContext } from '../AuthContext';
import Notification from '../components/Notification';
import { API_BASE_URL } from '../config';

export const MachineGroupContext = createContext({
  machineGroups: [],
  loading: false,
  error: null,
  fetchMachineGroups: async () => {},
  createMachineGroup: async () => {},
  updateMachineGroup: async () => {},
  deleteMachineGroup: async () => {},
  addMachineToGroup: async () => {},
  removeMachineFromGroup: async () => {},
});


const MachineGroupProvider = ({ children }) => {
  const { authData } = useContext(AuthContext);
  const [machineGroups, setMachineGroups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [notifications, setNotifications] = useState([]);

  const addNotification = (message, type) => {
    const id = Date.now();
    setNotifications((prev) => [...prev, { id, message, type }]);
  };

  const showSuccessNotification = (message) => {
    addNotification(message, 'success');
  };

  const showErrorNotification = (message, requestBody) => {
    addNotification(`${message}. Request: ${JSON.stringify(requestBody)}`, 'error');
  };

  const removeNotification = (id) => {
    setNotifications((prev) =>
      prev.filter((notification) => notification.id !== id)
    );
  };

  // Fetch machines for a specific group
  const fetchGroupMachines = async (groupId) => {
    try {
      const response = await fetch(`${API_BASE_URL}/machines/groups/${groupId}`, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${authData.accessToken}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch machines for group ${groupId}`);
      }

      return await response.json();
    } catch (error) {
      console.error(`Error fetching machines for group ${groupId}:`, error);
      return { id: groupId, name: '', machineIds: [] };
    }
  };

  // Fetch machine groups
  const fetchMachineGroups = async () => {
    if (authData?.accessToken) {
      setLoading(true);
      setError(null);
      try {
        // First, fetch the list of groups
        const response = await fetch(`${API_BASE_URL}/machines/groups/`, {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${authData.accessToken}`,
            'Content-Type': 'application/json',
          },
        });

        if (!response.ok) {
          throw new Error('Failed to fetch machine groups');
        }

        const groupsData = await response.json();
        
        // Then fetch machines for each group
        const groupsWithMachines = await Promise.all(
          groupsData.map(async (group) => {
            const groupDetails = await fetchGroupMachines(group.id);
            return {
              ...group,
              machineIds: groupDetails.machineIds || []
            };
          })
        );

        setMachineGroups(groupsWithMachines);
      } catch (error) {
        setError(error.message);
      } finally {
        setLoading(false);
      }
    }
  };

  // Create a new machine group
  const createMachineGroup = async (groupData) => {
    if (authData?.accessToken) {
      try {
        const response = await fetch(`${API_BASE_URL}/machines/groups/`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${authData.accessToken}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(groupData),
        });

        if (!response.ok) {
          throw new Error('Failed to create machine group');
        }

        const newGroup = await response.json();
        setMachineGroups((prev) => [...prev, newGroup]);
        showSuccessNotification(`Machine group created: ${newGroup.name}`);
      } catch (error) {
        showErrorNotification(error.message, groupData);
      }
    }
  };

  // Update an existing machine group (sends only name in body; id is in URL)
  const updateMachineGroup = async (updatedGroup) => {
    if (authData?.accessToken) {
      try {
        const response = await fetch(
          `${API_BASE_URL}/machines/groups/${updatedGroup.id}`,
          {
            method: 'PUT',
            headers: {
              Authorization: `Bearer ${authData.accessToken}`,
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ name: updatedGroup.name }),
          }
        );

        if (!response.ok) {
          throw new Error('Failed to update machine group');
        }

        const updated = await response.json();
        setMachineGroups((prev) =>
          prev.map((group) => (group.id === updated.id ? updated : group))
        );

        showSuccessNotification(`Machine group updated: ${updated.name}`);
      } catch (error) {
        showErrorNotification(error.message, updatedGroup);
      }
    }
  };

  // Delete a machine group
  const deleteMachineGroup = async (groupId) => {
    if (authData?.accessToken) {
      try {
        const response = await fetch(`${API_BASE_URL}/machines/groups/${groupId}`, {
          method: 'DELETE',
          headers: {
            Authorization: `Bearer ${authData.accessToken}`,
            'Content-Type': 'application/json',
          },
        });

        if (!response.ok) {
          throw new Error('Failed to delete machine group');
        }

        setMachineGroups((prev) => prev.filter((group) => group.id !== groupId));
        showSuccessNotification('Machine group deleted successfully');
      } catch (error) {
        showErrorNotification(error.message, { groupId });
      }
    }
  };

  // Add a machine to a group
  const addMachineToGroup = async (machineId, groupId) => {
    if (authData?.accessToken) {
      try {
        const response = await fetch(
          `${API_BASE_URL}/machines/${machineId}/groups/${groupId}`,
          {
            method: 'POST',
            headers: {
              Authorization: `Bearer ${authData.accessToken}`,
              'Content-Type': 'application/json',
            }
          }
        );

        if (!response.ok) {
          throw new Error('Failed to add machine to group');
        }

        // Refresh the group data from the server to get accurate machine list
        const updatedGroupData = await fetchGroupMachines(groupId);
        
        // Update local state with the fresh data
        setMachineGroups(prevGroups =>
          prevGroups.map(group => 
            group.id === groupId ? updatedGroupData : group
          )
        );

        showSuccessNotification('Machine added to group successfully');
      } catch (error) {
        showErrorNotification(error.message, { machineId, groupId });
        throw error;
      }
    }
  };

  // Remove a machine from a group
  const removeMachineFromGroup = async (machineId, groupId) => {
    if (authData?.accessToken) {
      try {
        const response = await fetch(
          `${API_BASE_URL}/machines/${machineId}/groups/${groupId}`,
          {
            method: 'DELETE',
            headers: {
              Authorization: `Bearer ${authData.accessToken}`,
              'Content-Type': 'application/json',
            }
          }
        );

        if (!response.ok) {
          throw new Error('Failed to remove machine from group');
        }

        // Refresh the group data from the server to get accurate machine list
        const updatedGroupData = await fetchGroupMachines(groupId);
        
        // Update local state with the fresh data
        setMachineGroups(prevGroups =>
          prevGroups.map(group => 
            group.id === groupId ? updatedGroupData : group
          )
        );

        showSuccessNotification('Machine removed from group successfully');
      } catch (error) {
        showErrorNotification(error.message, { machineId, groupId });
        throw error;
      }
    }
  };

  useEffect(() => {
    fetchMachineGroups();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authData?.accessToken]);

  return (
    <MachineGroupContext.Provider
      value={{
        machineGroups,
        loading,
        error,
        fetchMachineGroups,
        createMachineGroup,
        updateMachineGroup,
        deleteMachineGroup,
        addMachineToGroup,
        removeMachineFromGroup,
      }}
    >
      {children}
      <div className="notification-container">
        {notifications.map((notification) => (
          <Notification
            key={notification.id}
            message={notification.message}
            type={notification.type}
            onClose={() => removeNotification(notification.id)}
          />
        ))}
      </div>
    </MachineGroupContext.Provider>
  );
};

export default MachineGroupProvider;
